import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Filter } from 'lucide-react';
import { useSchedulingContext } from '../../pages/schedules/SchedulingCanvas';
import { useCoverageAnalytics, useScheduleIntervals } from '../../hooks/useScheduleIntervals';

export default function StaffingCoverageChart() {
  const {
    channelFilter,
    setChannelFilter,
    teamFilter,
    setTeamFilter,
    shiftFilter,
    setShiftFilter,
    setCursorPosition,
    scheduleId,
    selectedDate,
  } = useSchedulingContext();

  // Available teams and shifts (in production, fetch from API)
  const teams = ['ALL', 'Technical Support', 'Phone Support', 'Live Chat', 'Email Support'];
  const shifts = ['ALL', 'Morning (6AM-2PM)', 'Day (9AM-5PM)', 'Evening (2PM-10PM)', 'Night (10PM-6AM)'];

  // Fetch schedule intervals to calculate actual staffing
  const { data: scheduleIntervals = [] } = useScheduleIntervals({
    scheduleId: scheduleId || undefined,
    date: selectedDate,
    channel: channelFilter === 'TOTAL' ? undefined : channelFilter,
  });

  // Fetch real coverage data from API
  const { data: analyticsData, isLoading } = useCoverageAnalytics({
    scheduleId: scheduleId || undefined,
    date: selectedDate,
    channel: channelFilter === 'TOTAL' ? undefined : channelFilter,
  });

  // Transform data for chart with demo fallback
  const coverageData = useMemo(() => {
    console.log('StaffingCoverageChart - analyticsData:', analyticsData);
    console.log('StaffingCoverageChart - scheduleId:', scheduleId);
    console.log('StaffingCoverageChart - selectedDate:', selectedDate);
    console.log('StaffingCoverageChart - scheduleIntervals:', scheduleIntervals.length);

    // If we have real data, use it
    if (analyticsData?.hourly && analyticsData.hourly.length > 0) {
      console.log('Using real coverage data:', analyticsData.hourly.length, 'hours');
      return analyticsData.hourly.map((item) => ({
        ...item,
        // Add a property to determine bar color based on coverage
        isMeetingDemand: item.actual >= item.required,
      }));
    }

    // Calculate actual staffing from schedule intervals
    // Count unique agents scheduled for each hour
    const actualStaffingByHour: { [hour: number]: Set<string> } = {};

    scheduleIntervals.forEach((interval: any) => {
      const startHour = Math.floor(interval.intervalStart / 4);
      const endHour = Math.floor(interval.intervalEnd / 4);

      for (let hour = startHour; hour < endHour; hour++) {
        if (!actualStaffingByHour[hour]) {
          actualStaffingByHour[hour] = new Set();
        }
        actualStaffingByHour[hour].add(interval.agentId);
      }
    });

    console.log('Calculated actual staffing by hour:', actualStaffingByHour);

    // Generate demo data for 24 hours
    console.log('Generating demo staffing coverage data for', selectedDate.toDateString());
    const demoData = [];

    for (let hour = 0; hour < 24; hour++) {
      const hourStr = `${hour.toString().padStart(2, '0')}:00`;

      // Simulate varying demand throughout the day
      let baseRequired = 10;

      // Peak hours: 9am-5pm
      if (hour >= 9 && hour <= 17) {
        baseRequired = 45;
      }
      // Shoulder hours: 7-9am and 5-7pm
      else if ((hour >= 7 && hour < 9) || (hour >= 17 && hour < 19)) {
        baseRequired = 30;
      }
      // Late night: 11pm-6am
      else if (hour >= 23 || hour < 6) {
        baseRequired = 5;
      }
      // Early morning/evening
      else {
        baseRequired = 15;
      }

      // Add deterministic variance based on hour so data doesn't change on re-render
      const variance = (hour * 7) % 11 - 5; // Deterministic: -5 to +5
      const required = Math.max(1, baseRequired + variance);

      // Get actual staffing from schedule intervals, or use deterministic demo data
      let actual = 0;
      if (actualStaffingByHour[hour]) {
        actual = actualStaffingByHour[hour].size;
      } else {
        // Fallback to deterministic demo data if no intervals
        const actualVariance = (hour % 10) < 7
          ? (hour % 5) // Slightly over or meeting demand (0-4)
          : -((hour % 8) + 1); // Understaffed (-1 to -8)
        actual = Math.max(0, required + actualVariance);
      }

      demoData.push({
        hour: hourStr,
        required,
        actual,
        isMeetingDemand: actual >= required,
      });
    }

    console.log('Generated demo coverage data:', demoData.length, 'hours');
    console.log('Demo data sample:', demoData.slice(0, 3));
    return demoData;
  }, [analyticsData, selectedDate, scheduleIntervals]);

  console.log('Final coverageData being rendered:', coverageData.length, 'hours', coverageData.slice(0, 2));

  const handleMouseMove = (e: any) => {
    if (e && e.activeLabel) {
      // Extract hour from label (e.g., "09:00" -> 9)
      const hour = parseInt(e.activeLabel.split(':')[0]);
      setCursorPosition(hour);
    }
  };

  const handleMouseLeave = () => {
    setCursorPosition(null);
  };

  // Loading state only (demo data will be generated if no real data)
  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-gray-500">Loading coverage data...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Staffing Coverage</h2>
          <p className="text-sm text-gray-500">Required vs Actual staffing by hour</p>
        </div>

        {/* Channel Filter Buttons */}
        <div className="flex items-center space-x-2">
          {(['TOTAL', 'PHONE', 'CHAT', 'EMAIL'] as const).map((channel) => (
            <button
              key={channel}
              onClick={() => setChannelFilter(channel)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                channelFilter === channel
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {channel.charAt(0) + channel.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Additional Filters Row */}
      <div className="flex items-center space-x-3 mb-4 pb-3 border-b border-gray-200">
        <Filter className="h-4 w-4 text-gray-500" />

        {/* Team Filter */}
        <select
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
          className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {teams.map((team) => (
            <option key={team} value={team}>
              {team === 'ALL' ? 'All Teams' : team}
            </option>
          ))}
        </select>

        {/* Shift Filter */}
        <select
          value={shiftFilter}
          onChange={(e) => setShiftFilter(e.target.value)}
          className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {shifts.map((shift) => (
            <option key={shift} value={shift}>
              {shift === 'ALL' ? 'All Shifts' : shift}
            </option>
          ))}
        </select>

        {/* Active filters badge */}
        {(teamFilter !== 'ALL' || shiftFilter !== 'ALL') && (
          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
            Filters active
          </span>
        )}
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={200}>
        <BarChart
          data={coverageData}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="hour"
            tick={{ fontSize: 12 }}
            stroke="#9ca3af"
          />
          <YAxis
            tick={{ fontSize: 12 }}
            stroke="#9ca3af"
            label={{ value: 'Agents', angle: -90, position: 'insideLeft', fontSize: 12 }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '12px',
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: '12px' }}
          />
          <Bar
            dataKey="required"
            fill="#93c5fd"
            name="Required"
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="actual"
            name="Actual"
            radius={[4, 4, 0, 0]}
          >
            {coverageData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.actual >= entry.required ? '#34d399' : '#f87171'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend with color coding explanation */}
      <div className="flex items-center justify-center space-x-6 mt-2 text-xs text-gray-600">
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#93c5fd' }} />
          <span>Required</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#34d399' }} />
          <span>Meeting Demand</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#f87171' }} />
          <span>Understaffed</span>
        </div>
      </div>
    </div>
  );
}
